import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker as DayPicker } from '@mui/x-date-pickers/DatePicker';
import { HelperTextOutside } from './TextField/HelperTextOutside'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { TextField } from '@material-ui/core';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown'
import moment from 'moment'
import clsx from 'clsx';
import { popperStyles, useStyles } from './DatePickerStyles';
import { isMobile as isMobileNative } from 'react-device-detect';
import InputAdornment from '@mui/material/InputAdornment';
import { formatDateUtc, settingMomentES } from 'Utilities';
import './DatePicker.less'

export const sizes = {
    small: 'small',
    large: 'large',
}

const YYYY_MM_DD = 'YYYY-MM-DD'
const YYYYMMDD = 'YYYY/MM/DD'
const DDMMYYYY = 'DD/MM/YYYY'

const ErrorText = {
    INVALID: 'Esta fecha no es válida.',
}

const OpenPickerIcon = ({size, disabled}) =>(
    <span 
        className={`material-symbols-rounded ${disabled ? 'bumperDisabledCalendarIcon' : 'bumperCalendarIcon'}`}
        style={{fontSize: size === sizes.large ? '24px' : '18px'}}
    >event</span>
)

const RangeDate = {
    Min: moment('1900-01-01'),
    Max: moment('2099-12-31'),
}

const DatePicker = ({
    disabled,
    label,
    helperText: helperTextProp,
    error: errorProp = false,
    minDate,
    maxDate,
    selected,
    value: valueProp,
    onChange,
    onClick,
    placeholderText = 'dd/mm/aaaa',
    popperPlacement = 'bottom',
    className = '',
    size = 'small',
    i,
    isOptional= false,
    clearField = null,
    setClearfieldValue = null,
    disableHighlightToday = false
}) => {
    const [open, setOpen] = useState(false)
    const [helperText, setHelperText] = useState(helperTextProp)
    const [error, setError] = useState(errorProp)
    const [value, setValue] = useState()
    const inputRefMobile = useRef(null); // Ref para el input móvil
    const classes = useStyles({open, size});
    
    const valueMinDate = formatDateUtc(minDate || RangeDate.Min, YYYY_MM_DD)
    const valueMaxDate = formatDateUtc(maxDate || RangeDate.Max, YYYY_MM_DD)
    
    const isValidRangeDate = (date) => 
        date.isSameOrAfter(valueMinDate) &&
        date.isSameOrBefore(valueMaxDate)
    
    const setErrorMessage = (message) => {
        if (!message) {
            setHelperText(helperTextProp)
            setError(errorProp)
            return
        }
        setHelperText(message)
        setError(true)
    }

    const clearErrorMessage = () => setErrorMessage(null)
    
    useEffect(() => { settingMomentES() }, []) // reconfigura el formato de nombres de los meses en español

    useEffect(() => {
        if (errorProp === false) {
            clearErrorMessage()
            setError(false)
        } else if (errorProp && helperTextProp) {
            setErrorMessage(helperTextProp)
        }
    }, [errorProp, helperTextProp])

    useEffect(() => {
        if (!!value && !isValidRangeDate(value)) {
            setErrorMessage(ErrorText.INVALID)
        }
    }, [])

    useEffect(() => {
        if (selected) {
            setValue(moment(selected))
        } else if (valueProp) {
            setValue(moment(valueProp))
        } else {
            setValue(null)
            if (isMobileNative) {
                inputRefMobile.current.value = null
            }
        }
    }, [selected, valueProp])

    useEffect(()=>{
        clearFieldValue()
        clearErrorMessage()
    },[clearField])

    const isValidDate = (d) => {
        const date = moment(d, DDMMYYYY, true)
        return date.isValid() && isValidRangeDate(date)
    }
    const clearFieldValue = () => {
        let fieldToClear = document.getElementById('fieldInputToClear')
        if (fieldToClear){
            fieldToClear.addEventListener('input',(event) => {
                const formatRegex = /^\d+$/;
                if(!formatRegex.test(event.data)){
                    fieldToClear.value = fieldToClear.value.replace(/[^0-9/]/g, '')
                }
            })
            fieldToClear.value = null
        }
    }

    const handleBlur = ({target: { value: dateStr }}) => {
        if (!dateStr) return
        if (!isValidDate(dateStr)) {
            setErrorMessage(ErrorText.INVALID)
        }
    }

    const textField = (params) => {
        const iconMobile = isMobileNative ? {InputProps: {
            endAdornment: (
              <InputAdornment position="end">
                <OpenPickerIcon size={size} disabled={disabled} />
              </InputAdornment>
            ),
          }} : {}
        return <TextField {...params}
            variant='outlined'
            size={size}
            error={error}
            value={value ? formatDateUtc(value, YYYY_MM_DD):''}
            onBlur={handleBlur}
            onClick={ () => openPicker() }
            className={clsx(classes.textField)}
            inputProps={{
                ...params.inputProps,
                placeholder: placeholderText,
                value: clearField ? null : params.inputProps.value,
                id: clearField !== null ? 'fieldInputToClear' : 'fieldInput'
            }}
            {...iconMobile}            
        />}

        const isValidMinMaxDate = (vDate) => {
            const valueDate = formatDateUtc(vDate, YYYY_MM_DD)
            if (valueDate.length < 10) return false;
            return !(moment(valueDate).isBefore(moment(valueMinDate)))
                    && !(moment(valueDate).isAfter(moment(valueMaxDate)));
        }

    const openPicker = () => {
        if (disabled) return
        if (isMobileNative) {
        inputRefMobile.current.style.display = 'inline-block'
        inputRefMobile.current.focus()
        inputRefMobile.current.click()
        inputRefMobile.current.showPicker()
        } else {
            setOpen(true)
        }
    }

    const handleChange = d => {
        setClearfieldValue !== null && setClearfieldValue(false)
        clearErrorMessage()
        if (isMobileNative) {
            if (!d?._i) return
            if (!isValidMinMaxDate(d?._i)) {
                inputRefMobile.current.value = null
                setError(true)
                return
            }
        }
        const validDate = isValidDate(d)
        if (!validDate) {
            setError(true)
        }
        const formattedDate = validDate ? d.format(YYYYMMDD) : null
        if (!formattedDate) setError(false)
        onChange(formattedDate, i)
    }

    return (
        <div className={className}>
            <div>
                {label && (
                    <label disabled className={clsx(classes.label)}>
                        {label} {isOptional && <span>(opcional)</span>}
                    </label>
                )}
                <div>
                    <LocalizationProvider
                        dateAdapter={AdapterMoment}
                    >
                        <DayPicker
                            views={['year', 'month', 'day']}
                            defaultCalendarMonth={maxDate ? moment(maxDate) : null}
                            disabled={disabled}
                            onChange={handleChange}
                            open={open}
                            onOpen={() =>  { openPicker()  }}
                            onClose={() => { setOpen(false) }}
                            renderInput={textField}
                            value={value && formatDateUtc(value, YYYY_MM_DD)}
                            minDate={valueMinDate}
                            maxDate={valueMaxDate}
                            components={{
                                OpenPickerIcon: () => OpenPickerIcon({size, disabled}),
                                SwitchViewIcon: KeyboardArrowDown,
                            }}
                            PopperProps={{
                                placement: popperPlacement,
                                sx: {
                                    ...popperStyles,
                                    zIndex: '4000'
                                }
                            }}
                            className={clsx(classes.textField)}
                            disableHighlightToday={disableHighlightToday}
                        />
                    </LocalizationProvider>
                    <div className='nativeInputMobile' >
                        <input
                            type="date"
                            ref={inputRefMobile}
                            value={formatDateUtc(value, YYYY_MM_DD)}
                            onChange={ e => handleChange(moment(e.target.value))}
                            onClick={onClick}
                            min={valueMinDate}
                            max={valueMaxDate}                        />
                    </div>
                </div>
            </div>
            {error && (
                <HelperTextOutside size={size || 'small'} error helperText={helperText} />
            )}
            {!error && helperText && (
                <span className={clsx(classes.helperText)}>{helperText}</span>
            )}
        </div>
    )
}

DatePicker.propTypes = {
    size: PropTypes.oneOf(Object.values(sizes)),
    label: PropTypes.string,
    helperText: PropTypes.string,
    error: PropTypes.bool,
    selected: PropTypes.date,
    value: PropTypes.string,
    onChange: PropTypes.func,
    i: PropTypes.number,
}

DatePicker.defaultProps = {}

export default DatePicker